import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { resolvePostLoginDestination } from "@/lib/post-login-redirect.js";
import { ConfiguredRedirect } from "@/components/route/ConfiguredRedirect";
import { useAppInit } from "@/hooks/useAppInit";
import { useAuthStore, useTenantStore } from "@/store";
import { reporting } from "@/app/config.js";
import { ReportingProvider } from "@repo/reporting";
import { TableLoadingIndicatorProvider } from "@repo/ui/primitives/table-loading-indicator-context";
import { ProtectedRoute } from "@/components/route/ProtectedRoute";
import { LoadingLayout } from "@/components/layout/LoadingLayout";
import { MainLayout } from "@/components/layout/MainLayout";
import { PageSkeleton } from "@/components/common/PageSkeleton";
import LoginPage from "@/pages/login";
import SsoCallbackPage from "@/pages/auth/sso-callback";
import ActivateAccountPage from "@/pages/activate-account";
import ResetPasswordPage from "@/pages/auth/reset-password";
import NotFoundPage from "@/pages/not-found";
import { ReportStudioHandoffProvider } from "@/components/reports/ReportStudioHandoffProvider.jsx";
import { BrandTableLoadingMark } from "@/components/branding/BrandTableLoadingMark.jsx";
import { routeConfigs } from "./routesConfig";

// Loading component
function RedirectToLoginWithReturn() {
  const location = useLocation();
  return <Navigate to="/login" replace state={{ from: location }} />;
}

function AuthedLoginLandingRedirect() {
  const location = useLocation();
  const to = resolvePostLoginDestination(location);
  return <Navigate to={to} replace />;
}

/** Static boot splash — must not call useTranslation (used above i18n-ready Suspense). */
function BootSplash() {
  return (
    <div className="flex h-dvh w-full items-center justify-center bg-background p-[var(--ds-space-8)]">
      <div className="flex flex-col items-center gap-[var(--ds-space-control-gap)]">
        <div className="size-[var(--ds-density-control-height-sm)] animate-spin rounded-full border-4 border-primary border-t-transparent motion-reduce:animate-none" />
        <span className="animate-pulse text-sm text-muted-foreground motion-reduce:animate-none">Loading...</span>
      </div>
    </div>
  );
}

const LoadingSpinner = () => {
  const { t } = useTranslation("common");
  return (
    <div className="flex h-full w-full items-center justify-center p-[var(--ds-space-8)]">
      <div className="flex flex-col items-center gap-[var(--ds-space-control-gap)]">
        <div className="size-[var(--ds-density-control-height-sm)] animate-spin rounded-full border-4 border-primary border-t-transparent motion-reduce:animate-none" />
        <span className="animate-pulse text-sm text-muted-foreground motion-reduce:animate-none">{t("app.loading", { defaultValue: "Loading..." })}</span>
      </div>
    </div>
  );
};

/**
 * Main Application Component
 */
function AppContent() {
  useAppInit();
  const { i18n } = useTranslation();
  const { status, user, token } = useAuthStore();
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
  const [hydrated, setHydrated] = React.useState(false);

  // Wait for Zustand hydration from localStorage
  React.useEffect(() => {
    setHydrated(useAuthStore.persist.hasHydrated());
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    return unsub;
  }, []);

  // Show a structural loading layout while initializing or hydrating.
  // This prevents the "white screen" and premature redirects to /login.
  if (!hydrated || (status === "loading" && !token)) {
    return <LoadingLayout />;
  }

  return (
    <ReportingProvider
      host={reporting.origin}
      authToken={token || undefined}
      tenantId={selectedTenantId || undefined}
      locale={i18n.resolvedLanguage || i18n.language}
    >
      <ReportStudioHandoffProvider>
      <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          status === "authenticated" && user ? (
            <AuthedLoginLandingRedirect />
          ) : (
            <Suspense fallback={<LoadingSpinner />}>
              <LoginPage />
            </Suspense>
          )
        }
      />
      <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
      <Route
        path="/auth/activate"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <ActivateAccountPage />
          </Suspense>
        }
      />
      <Route
        path="/auth/reset-password"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />
      <Route
        path="/breach"
        element={
          status === "authenticated" ? (
            <Navigate to="/home" replace />
          ) : (
            <Suspense fallback={<LoadingSpinner />}>
              <LoginPage />
            </Suspense>
          )
        }
      />

      {/* Protected Routes wrapped in MainLayout */}
      <Route
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingLayout />}>
              <MainLayout />
            </Suspense>
          </ProtectedRoute>
        }
      >
        {routeConfigs.map(({ path, component: Component, redirectTo, protected: isProtected }) => {
          if (!isProtected) return null;

          return (
            <Route
              key={path}
              path={path}
              element={
                redirectTo ? (
                  <ConfiguredRedirect to={redirectTo} />
                ) : (
                  Component && (
                    <Suspense fallback={<PageSkeleton />}>
                      <Component />
                    </Suspense>
                  )
                )
              }
            />
          );
        })}
      </Route>

      {/* Handle any public routes from config if any */}
      {routeConfigs.map(({ path, component: Component, redirectTo, protected: isProtected }) => {
        if (isProtected) return null;
        return (
          <Route
            key={path}
            path={path}
            element={
              redirectTo ? (
                <ConfiguredRedirect to={redirectTo} />
              ) : (
                Component && (
                  <Suspense fallback={<LoadingSpinner />}>
                    <Component />
                  </Suspense>
                )
              )
            }
          />
        );
      })}

      <Route
        path="*"
        element={
          token ? (
            <ProtectedRoute>
              <Suspense fallback={<LoadingLayout />}>
                <MainLayout />
              </Suspense>
            </ProtectedRoute>
          ) : (
            <RedirectToLoginWithReturn />
          )
        }
      >
        <Route
          path="*"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Route>
      </Routes>
      </ReportStudioHandoffProvider>
    </ReportingProvider>
  );
}

function App() {
  return (
    <Router>
      <TableLoadingIndicatorProvider indicator={BrandTableLoadingMark}>
        <Suspense fallback={<BootSplash />}>
          <AppContent />
        </Suspense>
      </TableLoadingIndicatorProvider>
    </Router>
  );
}

export default App;
